import { sharedEndpoint } from "./shared";
import { BRANCH_OPTIONS } from "../../utils/constants";
import { ORDER_SOURCE } from "../../data/constants";

/**
 * apps/web's endpoint map: the 119 shared keys + web's 4 divergent keys + 46 web-only keys.
 *
 * ⚠️ Every section below spreads its shared half FIRST (`...sharedEndpoint.POS`). Dropping
 * that spread silently removes every shared key in the section — clean typecheck, green
 * build, 404s on a live terminal. Verify with:
 *   node scripts/endpoints-diff.mjs <old apps/web/src/config/endpoints.js> <this file>
 */
export const ENDPOINT = {
  ...sharedEndpoint,
  POS: {
    ...sharedEndpoint.POS,
    fetchBarcodeCustomer: `customer/specialmerchant`,
    getMostSoldItems: () => 'analytics/item/frequent',
    // `getXenoCouponRedeemedData` MOVED to shared.ts on 2026-09-08 — the customer-summary uplift
    // took the Redeemed Coupons tab cross-platform. Identical string, so this map is unchanged.
  },
  // `SALE.saleReopen` MOVED to shared.ts on 2026-09-29 (My Orders v2 re-opens on both platforms);
  // the section had no other web-only key, so the top-level spread above now supplies it.
  // `COMMON.getReleaseNotes` MOVED to shared.ts on release_4.0.0 (What's New on both platforms),
  // likewise its section's only web-only key.
  DISCOUNT: {
    ...sharedEndpoint.DISCOUNT,
    approvalStatus: () => 'sale/approval/status',
  },
  LOGIN: {
    ...sharedEndpoint.LOGIN,
    fetchLatestVersion: () => `device/app/latest`,
    verifyPin: () => "auth/verify-pin",
    checkPinExpiry: () => "auth/expiry",
  },
  PAYMENT: {
    ...sharedEndpoint.PAYMENT,
    extPaymentBalance: (id: string) => `payment/provider/${id}/balance`,
    extPaymentOtp: (id: string) => `payment/provider/${id}/otp`,
    extAddBalanceLink: (id: string) => `payment/provider/${id}/walletLink`,
    extPaymentRefund: `payment/ext/refund`,
  },
  LOYALTY: {
    ...sharedEndpoint.LOYALTY,
    // `apiOfferRedeem` + `unBlockDiscount` moved to ./shared with `packages/core/discounts`.
    // `customerCoupon` stays: its only caller is web's `components/XenoModal`.
    customerCoupon: () => `customer/offers`,
  },
  TABLEVIEW: {
    ...sharedEndpoint.TABLEVIEW,
    calculateSale: 'sale/calculate',
    tableReservation: () => 'sale/resource/branch/current',
    getSlotsInfo: () => `reservation/summary/date`,
    fetchResourceWithReservation: () => `reservation/resource/filter`,
    getReservationOfSlot: () => `reservation/filter`,
    fetchReservation: () => `reservation/fetch`,
    reservationSave: () => `reservation/save`,
    cancelReservation: (id: string) => `reservation/${id}/cancel`,
    getReservationSettings: (branchId: string) => `reservation/settings?branchId=${branchId}`,
  },
  MYORDER: {
    ...sharedEndpoint.MYORDER,
  },
  ITEMSTATUS: {
    ...sharedEndpoint.ITEMSTATUS,
    // DIVERGENT: web appends `&platform=`; mobile has no platform param.
    getAllInStockItems: (id: string, type: string, orderSource: string) => `item/outofstock/inStockItems?branchId=${id}&itemType=${type}&platform=${orderSource === ORDER_SOURCE.ALL ? '' : orderSource}`,
    getCurrentSoldOutItems: () => "branch/item/outofstock/filter",
  },
  OUTLETSTATUS: {
    ...sharedEndpoint.OUTLETSTATUS,
    // DIVERGENT: web hits the `/new` variant of this endpoint, mobile does not.
    toggleBranch: (branchId: string, action: string) => `branch/${branchId}/${action === BRANCH_OPTIONS.close ? "close" : "open"}/new`,
  },
  NOTIFICATIONS: {
    ...sharedEndpoint.NOTIFICATIONS,
    notificationLog: () => "log",
    // DIVERGENT: mobile appends a `?_dc=` cache-buster, web does not.
    testNotification: () => `/notification/test`,
  },
  // Sections that exist only on web.
  RISTACARD: {
    fetch: () => `ristacard/pos/fetch`,
    validate: () => `ristacard/sale/validate`,
  },
  ZOMATO_COMPLAINT: "https://www.zomato.com/partners/onlineordering/customerIssues/",
  // `INVENTORY` MOVED to shared.ts on 2026-09-29 (My Orders v2's back-order sheet, both platforms);
  // the section had no other key, so the top-level spread above now supplies it.
  DOTPE: {
    enquiry: `api/merchant/external/enquiry`,
  },
   COMPANY: {
    filter: () => `company/filter`,
    save: () => `company/save`,
    fetch: (companyId:any) => `company/${companyId}/fetch`,
    remove: (companyId:any) => `company/${companyId}/delete`,
  },
};
